import { type KeyboardEvent, useRef } from "react";

export type SegmentOption<V extends string> = { value: V; label: string; className?: string; disabled?: boolean };

type Props<V extends string> = {
  /** Accessible name of the group, e.g. the rule it controls. */
  label: string;
  options: ReadonlyArray<SegmentOption<V>>;
  value: V;
  onChange: (value: V) => void;
  /** id of an element that explains the choice (e.g. "always off"). */
  describedBy?: string;
  /** While a change is saving: the control stays focusable but ignores input. */
  busy?: boolean;
};

/**
 * Segmented control following the WAI-ARIA radio group pattern: one tab stop, arrow keys move and
 * select (skipping disabled options), Home/End jump to the ends.
 */
export function Segmented<V extends string>({ label, options, value, onChange, describedBy, busy }: Props<V>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
  const current = options.findIndex((o) => o.value === value);
  // Tab stop: the selected option, or the first enabled one if the selected one is missing.
  const tabStop = current >= 0 ? current : (enabled[0] ?? 0);

  function select(i: number) {
    const o = options[i];
    if (!o || o.disabled || busy) return;
    refs.current[i]?.focus();
    if (o.value !== value) onChange(o.value);
  }

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, i: number) {
    if (enabled.length === 0) return;
    const pos = enabled.indexOf(i);
    let next: number | undefined;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = enabled[(pos + 1) % enabled.length];
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = enabled[(pos - 1 + enabled.length) % enabled.length];
        break;
      case "Home":
        next = enabled[0];
        break;
      case "End":
        next = enabled[enabled.length - 1];
        break;
      default:
        return;
    }
    e.preventDefault();
    if (next !== undefined) select(next);
  }

  return (
    <div
      className="tri"
      role="radiogroup"
      aria-label={label}
      aria-describedby={describedBy}
      aria-busy={busy || undefined}
    >
      {options.map((o, i) => (
        // biome-ignore lint/a11y/useSemanticElements: segmented radio pattern; styled buttons with roving tabindex.
        <button
          key={o.value}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="radio"
          className={o.className}
          aria-checked={o.value === value}
          disabled={o.disabled}
          tabIndex={i === tabStop ? 0 : -1}
          onClick={() => select(i)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
